import { Dispatch, SetStateAction } from 'react';
import { useQuery } from '@apollo/client'
import { QUERY_MAP_PROP_CARD } from '../InfoWindow';
import { PageLink } from 'shared-components/Components/Link/web';
import { ImagenOptimizada } from 'shared-components/Components/Image/_new_web';
import { PriceTag } from 'shared-components/Components/Property/PriceTag/web';
import { Title } from 'shared-components/Components/Property/Title/web';
import { Favorite } from 'shared-components/Components/Property/Favorite/web';
import { LeadButtons } from 'shared-components/Components/Property/InformationRequestV2/LeadButtons/web';
import { formatMoney, showBathrooms, showBedrooms, showGuests, showM2, showRooms } from '@Utils/Functions';
import { MapCardSkeleton } from './MapCardSkeleton';
import { useWindowSize } from 'shared-components/GlobalHooks/useWindowSize';

const usePropertyQuery = (id) => {
    return useQuery(QUERY_MAP_PROP_CARD, {
        ssr: false,
        variables: { propID: id },
        fetchPolicy: "cache-first",
    });
};

interface MapCardProps {
    data: any,
    isActive: boolean,
    index?: number,
    currentIndex?: number,
    nextPreviousCallback?: (type, index) => void
}

export const MapCard = ({data, isActive, index, currentIndex, nextPreviousCallback} : MapCardProps) => {
    const screen = useWindowSize();
    const { data: dataLoaded, loading } = usePropertyQuery(data.id);
    const isDesktop = screen.lg || screen.xl;
    let property;
    if(dataLoaded) property = dataLoaded.property;

    const handleCallback = () => {
        if (nextPreviousCallback && isDesktop) {
            const type = index > currentIndex ? "next" : "";

            nextPreviousCallback(type, index)
        }
    }

    return (
        <>
            {(loading && !dataLoaded) ? <MapCardSkeleton />
            :(
                <div className={`map-card ${isActive ? "map-card--active" : ""}`}>
                    <PageLink
                        data={property}
                        as={property.link}
                        isBlank={true}
                        isActive={isDesktop ? isActive : true}
                        callback={handleCallback}
                    >
                        <div className='map-card__gallery'>
                            <ImagenOptimizada src={property.images[0]?.image} alt="Image" width={300} height={270} />
                        </div>

                        <div className='map-card__content'>
                            <div className="property-card-top">
                                <Favorite id={property.id} type={"justIcon"} />
                            </div>

                            <PriceTag
                                price={property.price}
                                operationType={property.operation_type}
                                temporalCurrency={property.temporal_currency}
                                temporalPrice={property.temporal_price}
                                commonExpenses={property.commonExpenses}
                            />

                            <Title title={property.title} hideInformation limitLength={isDesktop ? 2 : 1} level={5} />

                            <div className="lc-typologyTag">
                                {showBedrooms([Number(property.property_type.id)], Number(property.operation_type.id)) && (
                                    <span className="typology-bedrooms">
                                        <span>{property.bedrooms == 0 ? "Mono" : property.bedrooms + " Dorm."}</span>
                                    </span>
                                )}
                                {showRooms([Number(property.property_type.id)], Number(property.operation_type.id)) && (
                                    <span className="typology-rooms">
                                        <span>{property.rooms == 1
                                            ? property.rooms + " Ambiente"
                                            : property.rooms + " Ambientes"}</span>
                                    </span>
                                )}
                                {showBathrooms([Number(property.property_type.id)], Number(property.operation_type.id)) && (
                                    <span className="typology-bathrooms">
                                        <span>{property.bathrooms} {property.bathrooms > 1 ? "Baños" : "Baño"}</span>
                                    </span>
                                )}
                                {showM2(Number(property.operation_type.id)) && property.m2 > 0 && (
                                    <span className="typology-m2">
                                        <span>{formatMoney(property.m2)} m²</span>
                                    </span>
                                )}
                                {showGuests(Number(property.operation_type.id)) && property.guests && (
                                    <span className="typology-guests">
                                        <span>{property.guests} Huéspedes</span>
                                    </span>
                                )}
                            </div>

                            <LeadButtons
                                listing={property}
                                mode={"property"}
                                isInListing
                            />
                        </div>
                    </PageLink>
                </div>
            )}
        </>
    )
}